
<template>
  <div :style="{ margin: '2rem' }">
    <DialogLoading
      :message="loadingMessage"
      :active="loading"
    />
    <ConfirmationModal
      :active="confirmModal"
      :handleConfirm="handleAcceptConfirmModal"
      :handleCancel="handleCancelConfirmModal"
      title="Remover cliente"
      message="Você tem certeza que deseja deletar esse cliente?"
    />
    <v-card>
      <v-data-table
        :headers="headers"
        :items="clients"
        :options.sync="options"
        :server-items-length="total"
        :disable-sort="true"
        :loading="loading"
        loading-text="Carregando... Por favor, aguarde"
        no-data-text="Nenhum cliente encontrado"
        :footer-props="{
          showFirstLastPage: true,
          'items-per-page-text': 'registros por página',
          'items-per-page-options': [10, 50, 100, 5000],
          pageText: '{0}-{1} de {2}'
        }"
      >
          <template v-slot:top>
            <v-toolbar flat color="white">
              <v-toolbar-title class="title-bold">Cadastro de clientes</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn v-if="isAdmin" color="green" dark class="mb-2" v-on:click="exportExcel">
                Exportar
              </v-btn>

              <v-btn
                v-if="!isAdmin"
                color="primary"
                class="mb-2"
                :dark="releaseReached"
                :disabled="!releaseReached"
                @click="toFormPage"
              >
                Novo Cliente
              </v-btn>
            </v-toolbar>
            <v-container>
              <v-row>
                <v-col cols="12" sm="4" md="3">
                  <v-select
                    :items="statusesItems"
                    item-text="name"
                    item-value="id"
                    no-data-text="Sem dados"
                    label="Status"
                    v-model="filters.status"
                  />
                </v-col>
                <v-col cols="12" sm="4" md="3" v-if="isAdmin">
                  <v-select
                    :items="realStateItems"
                    item-text="name"
                    item-value="id"
                    no-data-text="Sem dados"
                    label="Imobiliária"
                    v-model="filters.realState"
                  />
                </v-col>
                <v-col cols="12" sm="4" md="3" v-if="isAdmin">
                  <v-select
                    :items="maritalStatusItems"
                    item-text="name"
                    item-value="id"
                    no-data-text="Sem dados"
                    label="Estado Civil"
                    v-model="filters.maritalStatus"
                  />
                </v-col>
                <v-col cols="12" sm="4" md="3" v-if="isAdmin">
                  <v-select
                    :items="propertiesItems"
                    no-data-text="Sem dados"
                    item-text="name"
                    item-value="id"
                    label="Empreendimento"
                    v-model="filters.property"
                  />
                </v-col>
                <v-col cols="12" sm="4" md="3" v-if="isAdmin">
                  <v-select
                    :items="dddsItems"
                    item-text="ddd_cell"
                    item-value="ddd_cell"
                    no-data-text="Sem dados"
                    label="DDD"
                    v-model="filters.ddd"
                  />
                </v-col>
                <v-col cols="12" sm="4" md="3" v-if="isAdmin">
                  <v-select
                    :items="typePersonItems"
                    item-text="name"
                    item-value="id"
                    no-data-text="Sem dados"
                    label="Tipo de Pessoa"
                    v-model="filters.typePerson"
                  />
                </v-col>
                <v-col cols="12" sm="4" md="3">
                  <v-text-field
                    label="Nome"
                    v-model="filters.name"
                  />
                </v-col>
                <v-col cols="12" sm="4" md="1">
                  <v-btn color="green" class="btn-filter" text @click="filterClients">Buscar</v-btn>
                </v-col>
                <v-col cols="12" sm="4" md="1">
                  <v-btn color="red" class="btn-filter" text @click="clearFilters">Limpar Filtros</v-btn>
                </v-col>
              </v-row>
            </v-container>
          </template>
          <template v-slot:[`item.renderStatus`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  small
                  class="mr-2"
                  :color="item.status === 1 ? 'gray' : item.status === 2 ? 'green' : 'red'"
                  :disabled="item.hasDocuments ? false : true"
                  v-bind="attrs"
                  v-on="on"
                  @click="viewDoc(item)"
                >
                  mdi-account-box
                </v-icon>
              </template>
              <span>{{ item.clientCnpj ? 'Ver documento(s)' : 'Ver documento' }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-if="isAdmin && [1, 3].includes(item.status)"
                  small
                  class="mr-2"
                  color="green"
                  v-bind="attrs"
                  v-on="on"
                  @click="acceptDoc(item)"
                >
                  mdi-spellcheck
                </v-icon>
              </template>
              <span>Aprovar</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-if="isAdmin && [1, 2].includes(item.status)"
                  small
                  class="mr-2"
                  color="red"
                  v-bind="attrs"
                  v-on="on"
                  @click="openRejectModal(item)"
                >
                  mdi-close
                </v-icon>
              </template>
              <span>Rejeitar</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-if="isAdmin"
                  small
                  class="mr-2"
                  color="blue"
                  v-bind="attrs"
                  v-on="on"
                  @click="viewLogs(item.id)"
                >
                  mdi-format-align-center
                </v-icon>
              </template>
              <span>Ver logs</span>
            </v-tooltip>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  small
                  class="mr-2"
                  v-bind="attrs"
                  v-on="on"
                  @click="edit(item)"
                >
                  mdi-circle-edit-outline
                </v-icon>
              </template>
              <span>Editar</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="openConfirmModal(item)"
                >
                  mdi-delete-empty
                </v-icon>
              </template>
              <span>Remover</span>
            </v-tooltip>

            <v-btn
              v-if="!item.reserveId && showButtonReserves"
              color="primary"
              x-small
              dark
              class="ml-2"
              @click="openReserveModal(item)"
            >
              {{ !!item.reserveId ? 'Editar' : 'Nova' }} Reserva
            </v-btn>
          </template>
          <template v-slot:no-data>
            Sem dados
          </template>
        </v-data-table>
        <div class="text-center pt-2">
      </div>
      <reject-modal @alter-status="alterStatus" :emailMessageReject.sync="emailMessageReject" :showModalReject.sync="showModalReject"/>
      <log-modal :dataLogs.sync="dataLogs" :showModalLog.sync="showModalLog"/>
      <reserve-modal
        :dialog.sync="dialog"
        :reserve.sync="reserve"
        :properties="properties"
        :brokers="brokerItems"
        :type-buys="typeBuys"
        :condition-payments="conditionPayments"
        :size-apartments="sizeApartments"
        :mode-create="reserve.id ? false : true"
        :is-gold-attendance="isGoldAttendance"
        :show-activator="false"
        @blur-client="getClientByCpfCnpj"
        @save="save"
        @close="close"
      />
    </v-card>
  </div>
</template>

<script>
import DialogLoading from '@/components/loading/DialogLoading'
import ConfirmationModal from '@/components/modals/ConfirmationModal'
import LogModal from '@/components/modals/LogModal'
import RejectModal from '@/components/modals/RejectModal'
import ReserveModal from '@/components/modals/ReserveModal'
import message from '@/utils/message'
import { normalizeFiles } from '@/utils/imageFile'
import store from '@/store'
import api from '@/services/api'
import xlsx from 'json-as-xlsx'
import moment from 'moment'

export default {
  components: {
    DialogLoading,
    ConfirmationModal,
    RejectModal,
    LogModal,
    ReserveModal
  },

  data: () => ({
    now: moment(),
    refreshNowInterval: null,
    total: 0,
    options: {
      page: 1,
      itemsPerPage: 10
    },
    optionsPage: [10, 25, 50, 100],
    loading: true,
    loadingMessage: 'Carregando',
    filters: {
      realState: null,
      status: null,
      ddd: null,
      typePerson: null,
      name: null,
      maritalStatus: null,
      property: null
    },
    headers: [
      { text: 'Nome', align: 'start', value: 'clientName' },
      { text: 'Corretor', value: 'brokerName' },
      { text: 'Imobiliária', value: 'realStateName' },
      { text: 'Status', value: 'renderStatus' },
      { text: 'Ações', value: 'actions', sortable: false }
    ],
    realStateItems: [],
    propertiesItems: [],
    statusesItems: [],
    dddsItems: [],
    maritalStatusItems: [
      { id: 1, name: 'Casado ou União Estável' },
      { id: 2, name: 'Demais Situações' }
    ],
    typePersonItems: [
      { id: 1, name: 'CPF' },
      { id: 2, name: 'CNPJ' }
    ],
    clients: [],
    confirmModal: false,
    itemToDelete: null,
    showModalReject: false,
    emailMessageReject: null,
    item: null,
    showModalLog: false,
    dataLogs: [],
    // Reserva (modal ReserveModal)
    dialog: false,
    properties: [],
    brokerItems: [],
    typeBuys: [],
    conditionPayments: [],
    sizeApartments: [],
    brokerLogged: store.getters.user.broker,
    reserve: {
      id: null,
      property: 2,
      client: { id: null, name: null },
      broker: null,
      typeAtendance: 1,
      numberLot: null,
      typeBuy: null,
      conditionPayment: null,
      identifierClient: null,
      documents: [],
      document: null,
      newDocument: null,
      status: { id: 1 },
      firstLot: null,
      secondLot: null,
      thirdLot: null,
      fourthLot: null,
      fifthLot: null,
      buildInTwoYears: null,
      sizeApartment: null,
      parkingSpace: null
    },
    defaultItem: {
      id: null,
      property: 2,
      client: { id: null, name: null },
      broker: null,
      typeAtendance: 1,
      numberLot: null,
      typeBuy: null,
      conditionPayment: null,
      identifierClient: null,
      documents: [],
      document: null,
      newDocument: null,
      status: { id: 1 },
      firstLot: null,
      secondLot: null,
      thirdLot: null,
      fourthLot: null,
      fifthLot: null,
      buildInTwoYears: null,
      sizeApartment: null,
      parkingSpace: null
    }
  }),

  computed: {
    isAdmin () {
      return store.getters.user.userType !== null && store.getters.user.userType.id === 1
    },
    isManager () {
      return store.getters.user.userType !== null && store.getters.user.userType.id === 3
    },
    showButtonReserves () {
      return !this.isAdmin && store.getters.config.showMenuReserves !== null && store.getters.config.showMenuReserves
    },
    isGoldAttendance () {
      return this.reserve.typeAtendance === 1 || (this.reserve.typeAtendance && this.reserve.typeAtendance.id === 1)
    },
    releaseReached () {
      return this.now.isSameOrAfter(moment('2026-08-05 18:00', 'YYYY-MM-DD HH:mm'))
    }
  },

  watch: {
    options: {
      handler () {
        this.getClients()
      },
      deep: true
    }
  },

  created () {
    this.refreshNowInterval = setInterval(() => {
      this.now = moment()
    }, 30000)

    if (this.isAdmin) {
      this.getDDDS()
    } else {
      this.filters.realState = store.getters.user.realState.id
      this.getClients()
    }

    this.getProperties()
    this.getStatuses()
    this.getRealStates()

    if (this.showButtonReserves) {
      this.getBrokers()
      this.getTypeBuys()
      this.getConditionPayments()
      this.getSizeApartments()
    }
  },

  beforeDestroy () {
    clearInterval(this.refreshNowInterval)
  },

  methods: {
    async getClients () {
      try {
        this.loading = true

        const { data } = await api.get(`/clients?page=${this.options.page}&itemsPerPage=${this.options.itemsPerPage}&name=${this.filters.name}&realState=${this.filters.realState}
        &status=${this.filters.status}&ddd=${this.filters.ddd}&typePerson=${this.filters.typePerson}&maritalStatus=${this.filters.maritalStatus}
        &property=${this.filters.property}`)

        this.clients = data.data
        this.total = Number(data.total)
      } catch (error) {
        message.error('Erro ao buscar clientes')
      } finally {
        this.loading = false
      }
    },

    filterClients () {
      this.options.page = 1
      this.getClients()
    },

    clearFilters () {
      this.filters.realState = null
      this.filters.status = null
      this.filters.ddd = null
      this.filters.maritalStatus = null
      this.filters.typePerson = null
      this.filters.name = null
      this.filters.property = null
      this.options.page = 1

      this.getClients()
    },

    async getProperties () {
      try {
        const { data } = await api.get('/properties')
        this.propertiesItems = data
      } catch (error) {
        message.error('Erro ao buscar empreendimentos')
      } finally {
        this.loading = false
      }
    },

    handleCancelConfirmModal () {
      this.confirmModal = false
    },

    handleAcceptConfirmModal () {
      this.confirmModal = false
      this.deleteItem()
    },

    openConfirmModal (item) {
      this.itemToDelete = item
      this.confirmModal = true
    },

    toFormPage () {
      if (!this.releaseReached) {
        return
      }

      this.$router.push({ name: 'createClient' })
    },

    async viewDoc (item) {
      try {
        const { data: dataClient } = await api.get(`/clients/${item.id}`)

        if (dataClient.documents.length === 0) {
          message.error('Nenhum documento cadastrado')
          return
        }

        this.openDocuments(dataClient.documents)
        // const { data } = await api.get(`/documents/${item.documentid}`)

        // if (data.type === 'image') {
        //   return this.openImage(data.base64)
        // }

        // this.openPdf(data.base64)
      } catch (error) {
        message.error(error.message)
      }
    },

    async viewLogs (id) {
      this.loading = true
      try {
        const { data } = await api.get(`clients/${id}/logs`)
        this.dataLogs = data
        this.showModalLog = true
      } catch (error) {
        message.error('Erro ao buscar logs')
      } finally {
        this.loading = false
      }
    },

    async acceptDoc (item) {
      this.loading = true
      try {
        await api.put(`clients/${item.id}/status`, { status_id: 2 })
        item.colorStatus = 'green'
        item.status = 2
        message.success('Status atualizado')
      } catch (error) {
        message.error(error.message)
      } finally {
        this.loading = false
      }
    },

    async rejectDoc () {
      try {
        this.loading = true
        await api.put(`clients/${this.item.id}/status`, { status_id: 3, emailMessage: this.emailMessageReject })
        this.item.colorStatus = 'red'
        this.item.status = 3
        message.success('Status atualizado')
      } catch (error) {
        message.error(error.message)
      } finally {
        this.resetEmailsInfos()
        this.loading = false
      }
    },

    // async downloadDoc (item) {
    //   try {
    //     const { data } = await api.get(`/documents/${item.documentid}`)
    //     window.location.href = 'data:application/octet-stream;base64,' + data.base64
    //   } catch (error) {
    //     message.error('Erro ao fazer o download')
    //   }
    // },

    async openRejectModal (item) {
      this.showModalReject = true
      this.item = item
    },

    async alterStatus () {
      this.showModalReject = false
      if (!this.emailMessageReject) {
        this.resetEmailsInfos()
        return message.error('Motivo: Mensagem do e-mail vazia', 'Status não atualizado')
      }
      this.rejectDoc()
    },

    async resetEmailsInfos () {
      this.emailMessageReject = null
      this.item = null
    },

    openImage (base64) {
      const image = new Image()
      image.src = 'data:image/png;base64,' + base64
      image.style.maxWidth = '100%'
      const w = window.open('')
      w.document.write(image.outerHTML)
    },

    openPdf (base64) {
      const blob = this.base64ToBlob(base64, 'application/pdf')
      const url = URL.createObjectURL(blob)
      const pdfWindow = window.open('')
      pdfWindow.document.write("<iframe width='100%' height='100%' src='" + url + "'></iframe>")
    },

    async openDocuments (documents) {
      try {
        this.loading = true
        this.loadingMessage = 'Carregando'

        const files = []

        for (const doc of documents) {
          const { data } = await api.get(`/documents/${doc.id}`)

          if (data.type === 'image') {
            const image = new Image()
            image.src = 'data:image/png;base64,' + data.base64
            image.style.maxWidth = '100%'

            files.push(image.outerHTML, '<br>')
          } else {
            const blob = this.base64ToBlob(data.base64, 'application/pdf')
            const url = URL.createObjectURL(blob)
            files.push("<iframe width='100%' height='100%' style='border: none; margin-bottom: 24px;' src='" + url + "'></iframe>")
          }
        }

        const w = window.open('', '_blank')
        w.document.write(...files)
      } catch (_error) {
        message.error('Erro ao carregar documentos')
      } finally {
        this.loading = false
      }
    },

    base64ToBlob (base64, type = 'application/octet-stream') {
      const binStr = atob(base64)
      const len = binStr.length
      const arr = new Uint8Array(len)
      for (let i = 0; i < len; i++) {
        arr[ i ] = binStr.charCodeAt(i)
      }
      return new Blob([ arr ], { type: type })
    },

    // openPdf (base64) {
    //   const pdfWindow = window.open('')
    //   pdfWindow.document.write(
    //     "<iframe width='100%' height='100%' src='data:application/pdf;base64, " +
    //     encodeURI(base64) + "'></iframe>"
    //   )
    // },

    async getStatuses () {
      try {
        const { data } = await api.get('/statuses')
        this.statusesItems = data
      } catch (error) {
        message.error('Erro ao buscar status')
      } finally {
        this.loading = false
      }
    },

    async getDDDS () {
      try {
        const { data } = await api.get('/clients/ddds')
        this.dddsItems = data
      } catch (error) {
        message.error('Erro ao buscar DDDs')
      } finally {
        this.loading = false
      }
    },

    async getRealStates () {
      try {
        const { data } = await api.get('/real-states?page=-1&itemsPerPage=-1')
        this.realStateItems = data.data

        if (data.data && data.data.length > 0) {
          this.properties = data.data[0].properties
          this.reserve.property = this.properties[0]
        }
      } catch (error) {
        message.error('Erro ao buscar imobiliárias')
      } finally {
        this.loading = false
      }
    },

    async exportExcel () {
      try {
        message.success('Excel sendo gerado. Em instantes será feito o download automático')
        const { data } = await api.post('/clients/excel', { filters: this.filters })
        const dataExcel = await this.getColumnsExcel()
        dataExcel[0].content = data

        const settings = {
          fileName: 'Clientes',
          extraLength: 3,
          writeOptions: {}
        }

        xlsx(dataExcel, settings)
      } catch (error) {
        console.warn(error)
        message.error('Erro ao gerar excel')
      }
    },

    async edit (item) {
      let routeData = this.$router.resolve({ path: `/clientes/${item.id}/editar` })
      window.open(routeData.href, '_blank')
    },

    async deleteItem () {
      this.loadingMessage = 'Removendo'
      this.loading = true

      try {
        await api.delete(`/clients/${this.itemToDelete.id}`)
        message.success('Cliente removido')
        this.removeToTable(this.itemToDelete)
      } catch (error) {
        message.error(error.message)
      } finally {
        this.loading = false
      }
    },

    removeToTable (item) {
      const index = this.clients.indexOf(item)
      this.clients.splice(index, 1)
      this.total = this.total - 1
    },

    async getColumnsExcel () {
      const data = [
        {
          sheet: 'Lista',
          columns: [
            { label: 'Data de Criação', value: row => row.created_at ? moment(row.created_at).format('DD/MM/YYYY HH:mm') : '' },
            { label: 'Nome', value: 'name' },
            { label: 'CPF', value: 'cpf' },
            { label: 'CNPJ', value: 'cnpj' },
            { label: 'Representante Legal', value: row => row.legal_representative ? row.legal_representative : '' },
            { label: 'Imobiliária', value: row => row.realState.name },
            { label: 'Corretor', value: row => row.broker.name },
            { label: 'Tamanho da Sala', value: row => row.sizeApartment ? row.sizeApartment.name : '' },
            // { label: 'Endereço Empresa', value: 'address_company' },
            { label: 'RG', value: 'rg' },
            { label: 'Nacionalidade', value: 'nationality' },
            { label: 'Profissão', value: 'profession' },
            { label: 'Endereço', value: row => row.address.street },
            { label: 'Número', value: row => row.address.number },
            { label: 'Complemento', value: row => row.address.complement },
            { label: 'Bairro', value: row => row.address.district },
            { label: 'CEP', value: row => row.address.cep },
            { label: 'Município', value: row => row.address.city },
            { label: 'DDD Residencial', value: row => row.ddd_telephone },
            { label: 'Número Residencial', value: row => row.telephone },
            { label: 'DDD Celular', value: row => row.country === 'BR' ? row.ddd_cell : '' },
            { label: 'Número Celular', value: row => row.cell },
            { label: 'E-mail', value: 'email' },
            { label: 'Estado Civil', value: row => row.maritalStatus.name },
            { label: 'Regime de Casamento', value: (row) => row.spouse ? row.spouse.propertyRegime.name : '' },
            { label: 'Nome do Cônjuge', value: (row) => row.spouse ? row.spouse.name : '' },
            { label: 'RG do Cônjuge', value: (row) => row.spouse ? row.spouse.rg : '' },
            { label: 'CPF do Cônjuge', value: (row) => row.spouse ? row.spouse.cpf : '' },
            { label: 'Nacionalidade do Cônjuge', value: (row) => row.spouse ? row.spouse.nationality : '' },
            { label: 'Profissão do Cônjuge', value: (row) => row.spouse ? row.spouse.profession : '' },
            { label: 'E-mail do Cônjuge', value: (row) => row.spouse ? row.spouse.email : '' },
            { label: 'DDD Celular do Cônjuge', value: (row) => row.spouse ? row.spouse.ddd_cell : '' },
            { label: 'Número Celular do Cônjuge', value: row => row.spouse ? row.spouse.cell : '' },
            { label: 'Endereço do Cônjuge', value: row => row.spouse ? row.spouse.address.street : '' },
            { label: 'Número do Cônjuge', value: row => row.spouse ? row.spouse.address.number : '' },
            { label: 'Complemento do Cônjuge', value: row => row.spouse ? row.spouse.address.complement : '' },
            { label: 'Bairro do Cônjuge', value: row => row.spouse ? row.spouse.address.district : '' },
            { label: 'CEP do Cônjuge', value: row => row.spouse ? row.spouse.address.cep : '' },
            { label: 'Município do Cônjuge', value: row => row.spouse ? row.spouse.address.city : '' }
          ]
        }
      ]

      // if (this.filters.maritalStatus === 1) {
      //   const fields = [
      //     { label: 'Regime de Casamento', value: (row) => { if (row.spouse) { return row.spouse.propertyRegime.name } return '' } },
      //     { label: 'Nome do Cônjuge', value: (row) => { if (row.spouse) { return row.spouse.name } return '' } },
      //     { label: 'RG do Cônjuge', value: (row) => { if (row.spouse) { return row.spouse.rg } return '' } },
      //     { label: 'CPF do Cônjuge', value: (row) => { if (row.spouse) { return row.spouse.cpf } return '' } },
      //     { label: 'Nacionalidade do Cônjuge', value: (row) => { if (row.spouse) { return row.spouse.nationality } return '' } },
      //     { label: 'Profissão do Cônjuge', value: (row) => { if (row.spouse) { return row.spouse.profession } return '' } },
      //     { label: 'E-mail do Cônjuge', value: (row) => { if (row.spouse) { return row.spouse.email } return '' } }
      //   ]
      //   data[0].columns = data[0].columns.concat(fields)
      // }

      return data
    },

    async openReserveModal (item) {
      if (item.reserveId) {
        message.error('Este cliente já possui uma reserva ativa. Para editar a reserva, acesse o menu "Reservas".')
        return
      }

      if (this.properties.length === 0) {
        message.error('Nenhum empreendimento cadastrado. Contate a Idealiza.')
        return
      }

      let broker = null

      if (this.isManager) {
        broker = { id: item.brokerId, name: item.brokerName }
      } else if (!this.isAdmin) {
        broker = store.getters.user.broker
      }

      this.reserve = {
        ...this.defaultItem,
        documents: [],
        client: { id: item.id, name: item.clientName || item.name },
        identifierClient: item.clientCnpj || item.clientCpf,
        property: this.properties[0],
        broker
      }

      this.dialog = true
    },

    async getClientByCpfCnpj () {
      try {
        if (this.reserve.identifierClient && (this.reserve.identifierClient.length === 14 || this.reserve.identifierClient.length === 18)) {
          const response = await api.get(`/clients/info?clientIdentifier=${this.reserve.identifierClient}`)

          if (response.data) {
            const { data } = await api.post('/reserves/validate', { clientIdentifier: this.reserve.identifierClient })
            if (!data) {
              message.error('Cliente já possui reserva ativa nessa imobiliária!')
              this.reserve.identifierClient = null
              this.reserve.client.id = null
              this.reserve.client.name = null
              return
            }

            this.reserve.client = {
              id: response.data.id,
              name: response.data.name
            }
            if (this.isAdmin || this.isManager) {
              this.reserve.broker = response.data.broker
            }
          } else {
            message.error('Nenhum cliente ativo vinculado a imobiliária com este CPF/CNPJ')
            this.reserve.identifierClient = null
            this.reserve.client.id = null
            this.reserve.client.name = null
          }
        }
      } catch (error) {
        message.error(error.message)
      } finally {
        this.loading = false
      }
    },

    async getBrokers () {
      try {
        const { data } = await api.get('/users?page=-1&itemsPerPage=-1')
        this.brokerItems = data.data
      } catch (error) {
        message.error('Erro ao buscar corretores')
      } finally {
        this.loading = false
      }
    },

    async getTypeBuys () {
      try {
        const response = await api.get('/type-buys')
        this.typeBuys = response.data
      } catch (error) {
        message.error('Erro ao buscar os tipos de compra')
      } finally {
        this.loading = false
      }
    },

    async getConditionPayments () {
      try {
        const response = await api.get('/condition-payments')
        this.conditionPayments = response.data
      } catch (error) {
        message.error('Erro ao buscar as condições de pagamento')
      } finally {
        this.loading = false
      }
    },

    async getSizeApartments () {
      try {
        const response = await api.get('/size-apartments')
        this.sizeApartments = response.data
      } catch (error) {
        message.error('Erro ao buscar tamanhos de apartamentos')
      } finally {
        this.loading = false
      }
    },

    async save () {
      if (!store.getters.config.showMenuReserves) {
        message.error('Ainda não é possível cadastrar reserva')
        return
      }

      this.loadingMessage = 'Salvando'
      this.loading = true

      let savedDocuments = []

      if (this.reserve.typeAtendance === 1) {
        if (Array.isArray(this.reserve.document)) {
          for (const doc of this.reserve.document) {
            const savedDoc = await this.createDocument(doc)
            if (!savedDoc) return
            savedDocuments = savedDoc
          }
        } else {
          const savedDoc = await this.createDocument(this.reserve.document)
          if (!savedDoc) return
          savedDocuments = savedDoc
        }
        if (!savedDocuments || savedDocuments.length === 0) return
      }

      this.createReserve(savedDocuments)
    },

    async createDocument (image) {
      try {
        const file = await normalizeFiles(image)
        const formData = new FormData()
        formData.append('images', file)
        formData.append('directory', 'reserves')

        const response = await api.post('documents', formData)
        this.reserve.documents.push(response.data)
        message.success('Documento salvo')
        return response.data
      } catch (error) {
        message.error('Erro ao salvar documento')
        message.error('Reserva não criada')
        return false
      } finally {
        this.loading = false
      }
    },

    getData () {
      const data = {
        id: this.reserve.id,
        property_id: this.reserve.property.id ? this.reserve.property.id : this.reserve.property,
        broker_id: this.reserve.broker.id ? this.reserve.broker.id : this.reserve.broker,
        client_id: this.reserve.client.id ? this.reserve.client.id : this.reserve.client,
        type_atendance_id: this.reserve.typeAtendance.id ? this.reserve.typeAtendance.id : this.reserve.typeAtendance,
        number_lot_id: 1,
        first_lot_id: this.reserve.firstLot ? this.reserve.firstLot.id ? this.reserve.firstLot.id : this.reserve.firstLot : this.reserve.firstLot,
        second_lot_id: null,
        third_lot_id: null,
        fourth_lot_id: null,
        fifth_lot_id: null,
        type_buy_id: this.reserve.typeBuy.id ? this.reserve.typeBuy.id : this.reserve.typeBuy,
        condition_payment_id: this.reserve.conditionPayment.id ? this.reserve.conditionPayment.id : this.reserve.conditionPayment,
        status_id: this.reserve.status.id,
        build_in_two_years: this.reserve.buildInTwoYears || false,
        size_apartment_id: this.reserve.sizeApartment ? this.reserve.sizeApartment.id ? this.reserve.sizeApartment.id : this.reserve.sizeApartment : this.reserve.sizeApartment,
        parking_space: this.reserve.parkingSpace || false,
        documents: this.reserve.documents
      }

      return data
    },

    async createReserve (savedDocuments) {
      try {
        let data = this.getData()
        data = {
          ...data,
          documents: savedDocuments.length > 0 ? savedDocuments : data.documents
        }
        await api.post('/reserves', data)
        message.success('Reserva salva')
        this.getClients()
      } catch (error) {
        message.error(error.message)
      } finally {
        this.loading = false
        this.close()
      }
    },

    close () {
      this.dialog = false
      this.reserve.identifierClient = null

      this.$nextTick(() => {
        this.reserve = {
          ...this.defaultItem,
          documents: [],
          property: this.properties[0],
          broker: !this.isAdmin ? store.getters.user.broker : null
        }
      })
    }
  }
}
</script>

<style scoped>
  .btn-filter {
    margin-top: 20px;
  }
</style>



// WEBPACK FOOTER //
// src/components/clients/Clients.vue